import { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";
import {
  WhatsappLogo,
  Clock,
  Truck,
  PaperPlaneRight,
} from "@phosphor-icons/react";
import { whatsappLink } from "@/data/site";
import { Link } from "react-router-dom";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls =
  "w-full bg-white border border-black/15 px-4 py-3.5 text-sm text-black placeholder:text-black/30 outline-none transition-colors duration-300 focus:border-[#FF5508]";

export const Contact = () => {
  const [form, setForm] = useState({ nombre: "", email: "", telefono: "", mensaje: "" });
  const [sending, setSending] = useState(false);

  const update = (field) => (e) => setForm({ ...form, [field]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/contact`, {
        nombre: form.nombre,
        email: form.email,
        telefono: form.telefono || null,
        mensaje: form.mensaje,
      });
      toast.success("Mensaje enviado. Te contactaremos muy pronto.");
      setForm({ nombre: "", email: "", telefono: "", mensaje: "" });
    } catch {
      toast.error("No pudimos enviar tu mensaje. Intenta por WhatsApp.");
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="contacto" data-testid="contact-section" className="py-28 lg:py-36 border-t border-black/10 bg-white">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-24">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
        >
        <p className="text-[11px] text-[#FF5508] mb-4">
            Contacto
          </p>
          <h2 className="font-display text-5xl sm:text-6xl uppercase font-semibold leading-none tracking-tight mb-8 text-[#111418]">
            Planea tu
            <br />
            <span className="text-outline">próxima ruta</span>
          </h2>
          <p className="text-black/55 text-base leading-relaxed max-w-md mb-10">
            Mándanos mensaje: te decimos disponibilidad, envío y todo lo que
            necesitas saber. Atención personalizada, sin vueltas.
          </p>

          <a
            data-testid="contact-whatsapp-cta"
            href={whatsappLink("Hola Ruta 360, quiero información sobre un producto.")}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-3 bg-[#25D366] text-white text-xs px-8 py-4 rounded-full transition-colors duration-300 hover:bg-black mb-12"
          >
            <WhatsappLogo size={18} weight="fill" />
            Escríbenos por WhatsApp
          </a>

          <div className="flex flex-col gap-4 text-xs text-black/50 mb-10">
            <span className="flex items-center gap-3">
              <Truck size={16} className="text-[#FF5508]" /> Envíos a todo México
            </span>
            <span className="flex items-center gap-3">
              <Clock size={16} className="text-[#FF5508]" /> Lun a Sáb 9:00 - 22:00 | Dom 10:00 - 20:00
            </span>
          </div>
        </motion.div>

        <motion.form
          data-testid="contact-form"
          onSubmit={submit}
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7, delay: 0.15 }}
          className="border border-black/10 bg-white p-8 lg:p-12 flex flex-col gap-6 shadow-[0_20px_60px_rgba(0,0,0,0.06)]"
        >
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            <input
              data-testid="contact-name-input"
              required
              value={form.nombre}
              onChange={update("nombre")}
              placeholder="Nombre"
              className={inputCls}
            />
            <input
              data-testid="contact-email-input"
              required
              type="email"
              value={form.email}
              onChange={update("email")}
              placeholder="Correo electrónico"
              className={inputCls}
            />
          </div>
          <input
            data-testid="contact-phone-input"
            value={form.telefono}
            onChange={update("telefono")}
            placeholder="Teléfono (opcional)"
            className={inputCls}
          />
          <textarea
            data-testid="contact-message-input"
            required
            rows={5}
            value={form.mensaje}
            onChange={update("mensaje")}
            placeholder="¿Qué producto te interesa o a dónde va tu próxima ruta?"
            className={`${inputCls} resize-none`}
          />
          <button
            data-testid="contact-submit-button"
            type="submit"
            disabled={sending}
            className="group inline-flex items-center justify-center gap-3 bg-black text-white text-xs px-8 py-4 rounded-full transition-colors duration-300 hover:bg-[#FF5508] disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {sending ? "Enviando…" : "Enviar mensaje"}
            <PaperPlaneRight
              size={16}
              className="transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-0.5"
            />
          </button>
          <p className="text-black/40 text-xs text-center">
            No vendemos ni compartimos tus datos.{" "}
            <Link to="/privacidad" data-testid="contact-privacidad-link" className="underline underline-offset-2 hover:text-black transition-colors">
              Aviso de privacidad
            </Link>
          </p>
        </motion.form>
      </div>
    </section>
  );
};
